import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectUserInfo, updateUserAsync } from '../userSlice';
import { useForm } from 'react-hook-form';
import { useLanguage } from '../../../context/LanguageContext';
import { useCommonTranslations } from '../../../context/commonTranslations';
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  ShieldCheckIcon,
  MapPinIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';

export default function UserProfile() {
  const dispatch = useDispatch();
  const userInfo = useSelector(selectUserInfo);
  const [selectedEditIndex, setSelectedEditIndex] = useState(-1);
  const [showAddAddressForm, setShowAddAddressForm] = useState(false);
  const [showAccountPassword, setShowAccountPassword] = useState(false);
  const { language } = useLanguage();
  const t = useCommonTranslations(language);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  const handleEdit = (addressUpdate, index) => {
    const newUser = { ...userInfo, addresses: [...userInfo.addresses] };
    newUser.addresses.splice(index, 1, addressUpdate);
    dispatch(updateUserAsync(newUser));
    setSelectedEditIndex(-1);
  };

  const handleRemove = (e, index) => {
    const newUser = { ...userInfo, addresses: [...userInfo.addresses] };
    newUser.addresses.splice(index, 1);
    dispatch(updateUserAsync(newUser));
  };

  const handleEditForm = (index) => {
    setSelectedEditIndex(index);
    const address = userInfo.addresses[index];
    setValue('name', address.name);
    setValue('email', address.email);
    setValue('contactNumber', address.contactNumber);
    setValue('city', address.city);
    setValue('state', address.state);
    setValue('pinCode', address.pinCode);
    setValue('phone', address.phone);
    setValue('street', address.street);
    setValue('accountUsername', address.accountUsername);
    setValue('accountPassword', address.accountPassword);
    setValue('contactEmail', address.contactEmail);
  };

  const handleAdd = (address) => {
    const newUser = { ...userInfo, addresses: [...userInfo.addresses, address] };
    dispatch(updateUserAsync(newUser));
    setShowAddAddressForm(false);
  };

  // Reusable form component
  const AddressForm = ({ onSubmit, onCancel, submitLabel }) => (
    <form
      className="bg-white rounded-xl border border-slate-200 p-6 mt-4"
      noValidate
      onSubmit={handleSubmit((data) => {
        onSubmit(data);
        reset();
      })}
    >
      <div className="space-y-6">
        {/* Personal Information Section */}
        <div className="pb-6 border-b border-slate-200">
          <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center gap-2">
            <UserIcon className="w-5 h-5 text-sky-500" />
            {t.profile.personalInfo}
          </h3>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                {t.profile.fullName} *
              </label>
              <input
                type="text"
                {...register('name', { required: t.profile.nameRequired })}
                id="name"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition text-slate-900 placeholder:text-slate-400"
                placeholder="John Doe"
              />
              {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                {t.profile.emailAddress} *
              </label>
              <input
                id="email"
                {...register('email', { required: t.profile.emailRequired })}
                type="email"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition text-slate-900 placeholder:text-slate-400"
                placeholder="john@example.com"
              />
              {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>}
            </div>

            <div>
              <label htmlFor="contactNumber" className="block text-sm font-medium text-slate-700 mb-2">
                {t.profile.contactNumber} *
              </label>
              <input
                id="contactNumber"
                {...register('contactNumber', { required: t.profile.contactRequired })}
                type="tel"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition text-slate-900 placeholder:text-slate-400"
                placeholder="+92-XXX-XXXXXXX"
              />
              {errors.contactNumber && <p className="mt-1 text-sm text-red-500">{errors.contactNumber.message}</p>}
            </div>
          </div>
        </div>

        {/* Account Information Section */}
        <div>
          <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center gap-2">
            <ShieldCheckIcon className="w-5 h-5 text-sky-500" />
            {t.profile.accountInfo}
          </h3>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="accountUsername" className="block text-sm font-medium text-slate-700 mb-2">
                {t.profile.accountUsername} *
              </label>
              <input
                type="text"
                {...register('accountUsername', { required: t.profile.usernameRequired })}
                id="accountUsername"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition text-slate-900 placeholder:text-slate-400"
                placeholder={t.profile.usernamePlaceholder}
              />
              {errors.accountUsername && <p className="mt-1 text-sm text-red-500">{errors.accountUsername.message}</p>}
            </div>

            <div>
              <label htmlFor="accountPassword" className="block text-sm font-medium text-slate-700 mb-2">
                {t.profile.accountPassword} *
              </label>
              <div className="relative">
                <input
                  type={showAccountPassword ? 'text' : 'password'}
                  {...register('accountPassword', { required: t.profile.passwordRequired })}
                  id="accountPassword"
                  className="w-full px-4 pr-12 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition text-slate-900 placeholder:text-slate-400"
                  placeholder={t.profile.passwordPlaceholder}
                />
                <button
                  type="button"
                  onClick={() => setShowAccountPassword(!showAccountPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showAccountPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.accountPassword && <p className="mt-1 text-sm text-red-500">{errors.accountPassword.message}</p>}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-4">
          {onCancel && (
            <button
              onClick={onCancel}
              type="button"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
              {t.common.cancel}
            </button>
          )}
          <button
            type="submit"
            className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-medium py-2.5 px-5 rounded-lg transition-all duration-200 text-sm"
          >
            <CheckIcon className="w-4 h-4" />
            {submitLabel}
          </button>
        </div>
      </div>
    </form>
  );

  return (
    <div className="min-h-screen bg-slate-100 py-8 sm:py-12">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Profile Header */}
        <div className="bg-white rounded-xl shadow-subtle border border-slate-200 p-6 sm:p-8 mb-6">
          <div className="flex items-start gap-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-sky-100 text-sky-600">
              <UserIcon className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <EnvelopeIcon className="w-6 h-6 text-slate-500" />
                <h1 className="text-2xl sm:text-3xl font-light tracking-tight text-slate-900">
                  {userInfo.email}
                </h1>
              </div>
              {userInfo.role === 'admin' && (
                <div className="mt-4">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-sky-100 text-sky-700 text-xs font-medium rounded-full">
                    <ShieldCheckIcon className="w-3.5 h-3.5" />
                    Admin
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Addresses Section */}
        <div className="bg-white rounded-xl shadow-subtle border border-slate-200 overflow-hidden">
          <div className="p-6 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-sky-100 text-sky-600">
                <MapPinIcon className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-medium text-slate-900">{t.profile.savedAddresses}</h2>
                <p className="text-sm text-slate-500">{t.profile.manageAddresses}</p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowAddAddressForm(true);
                setSelectedEditIndex(-1);
              }}
              className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-medium py-2.5 px-4 rounded-lg transition-all duration-200 text-sm"
            >
              <PlusIcon className="w-4 h-4" />
              {t.profile.addAccount}
            </button>
          </div>

          <div className="p-6">
            {/* Add Address Form */}
            {showAddAddressForm && (
              <AddressForm
                onSubmit={handleAdd}
                onCancel={() => setShowAddAddressForm(false)}
                submitLabel={t.profile.addAccount}
              />
            )}

            {/* Address Cards */}
            {userInfo.addresses.length > 0 ? (
              <div className="space-y-4 mt-4">
                {userInfo.addresses.map((address, index) => (
                  <div key={index}>
                    {/* Edit Form */}
                    {selectedEditIndex === index && (
                      <AddressForm
                        onSubmit={(data) => handleEdit(data, index)}
                        onCancel={() => setSelectedEditIndex(-1)}
                        submitLabel={t.profile.saveChanges}
                      />
                    )}

                    {/* Address Card */}
                    {selectedEditIndex !== index && (
                      <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 hover:border-sky-200 transition-colors">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                          <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {/* Personal Info */}
                            <div>
                              <p className="text-sm font-medium text-slate-900 mb-1">
                                {address.name}
                              </p>
                              <p className="text-sm text-slate-500 flex items-center gap-1.5">
                                <PhoneIcon className="w-3.5 h-3.5" />
                                {address.contactNumber || address.phone}
                              </p>
                            </div>

                            {/* Account Info */}
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                                {t.profile.account}
                              </p>
                              <p className="text-sm text-slate-700">
                                {address.accountUsername}
                              </p>
                            </div>

                            {/* Email */}
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                                {t.profile.email}
                              </p>
                              <p className="text-sm text-slate-700 truncate">
                                {address.email}
                              </p>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditForm(index)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-sky-600 hover:text-sky-700 hover:bg-sky-50 rounded-lg transition-colors"
                            >
                              <PencilIcon className="w-4 h-4" />
                              {t.common.edit}
                            </button>
                            <button
                              onClick={(e) => handleRemove(e, index)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <TrashIcon className="w-4 h-4" />
                              {t.common.remove}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              !showAddAddressForm && (
                <div className="text-center py-12">
                  <MapPinIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">{t.profile.noAddresses}</p>
                  <p className="text-sm text-slate-400 mt-1">
                    {t.profile.addAddressPrompt}
                  </p>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
